import request from './request'

/**
 * 分页查询订单列表
 * @param {object} data - 查询参数
 * @param {number} [data.planId] - 方案ID
 * @param {number} [data.status] - 状态
 * @param {string} [data.keyword] - 关键字（订单编号/产品名称）
 * @param {number} [data.page=1] - 页码，从1开始
 * @param {number} [data.size=20] - 每页大小
 */
export function getOrderPage(data) {
  return request.post('/order/page', data)
}

/**
 * 获取订单详情
 * @param {number} id - 订单ID
 */
export function getOrderDetail(id) {
  return request.get(`/order/${id}`)
}

/**
 * 更新订单状态
 * @param {object} data - 请求参数
 * @param {number} data.orderId - 订单ID
 * @param {number} data.status - 目标状态
 */
export function updateOrderStatus(data) {
  return request.put('/order/status', data)
}

/**
 * 删除订单
 * @param {number} id - 订单ID
 */
export function deleteOrder(id) {
  return request.delete(`/order/${id}`)
}

/**
 * 向已存在的方案中追加新订单
 * @param {object} data - 请求参数
 * @param {number} data.planId - 方案ID
 * @param {object} data.order - 订单信息
 * @param {number} data.order.productId - 产品ID
 * @param {string} data.order.orderType - 订单类型：ONCE/PERIODIC
 * @param {number} [data.order.periodCount] - 分期期数
 * @param {string} [data.order.serviceStart] - 服务开始时间
 * @param {string} [data.order.serviceEnd] - 服务结束时间
 * @param {number} data.order.amount - 订单金额
 * @param {string} [data.order.remark] - 备注
 * @param {number} [data.order.executorId] - 落地执行人员ID
 * @param {array} [data.order.billings] - 账单信息
 */
export function addOrderToPlan(data) {
  return request.post('/order/add-to-plan', data)
}

/**
 * 审批确认订单
 * @param {object} data - 请求参数
 * @param {number} data.orderId - 订单ID
 * @param {boolean} data.approved - 是否通过审批
 * @param {string} [data.reason] - 不通过原因（不通过时必填）
 */
export function approveOrder(data) {
  return request.put('/order/approve', data)
}

/**
 * 编辑订单
 * @param {object} data - 请求参数
 * @param {number} data.orderId - 订单ID
 * @param {number} data.productId - 产品ID
 * @param {string} data.orderType - 订单类型：ONCE/PERIODIC
 * @param {number} [data.periodCount] - 分期期数
 * @param {string} [data.serviceStart] - 服务开始时间
 * @param {string} [data.serviceEnd] - 服务结束时间
 * @param {number} data.amount - 订单金额
 * @param {string} [data.remark] - 备注
 * @param {array} [data.billings] - 账单信息
 */
export function editOrder(data) {
  return request.put('/order/edit', data)
}

/**
 * 更换订单执行人
 * @param {object} data - 请求参数
 * @param {number} data.orderId - 订单ID
 * @param {number} data.executorId - 新执行人ID
 */
export function changeOrderExecutor(data) {
  return request.put('/order/executor', data)
}

export function getOrderReminders(orderId) {
  return request.get(`/order/${orderId}/reminders`)
}

export function createOrderReminder(orderId, data) {
  return request.post(`/order/${orderId}/reminders`, data)
}

export function updateOrderReminder(orderId, reminderId, data) {
  return request.put(`/order/${orderId}/reminders/${reminderId}`, data)
}

export function enableOrderReminder(orderId, reminderId) {
  return request.put(`/order/${orderId}/reminders/${reminderId}/enable`)
}

export function disableOrderReminder(orderId, reminderId) {
  return request.put(`/order/${orderId}/reminders/${reminderId}/disable`)
}

export function deleteOrderReminder(orderId, reminderId) {
  return request.delete(`/order/${orderId}/reminders/${reminderId}`)
}

/**
 * 绑定订单资料
 * @param {number} orderMaterialId - 订单材料ID
 * @param {object} data - 请求参数
 * @param {number} data.orderId - 订单ID
 * @param {number} data.userMaterialId - 客户资料实例ID
 * @param {string} data.bindSource - 绑定来源，平台端固定 ADMIN
 */
export function bindOrderMaterial(orderMaterialId, data) {
  return request.post(`/order/materials/${orderMaterialId}/bindings`, data)
}

/**
 * 审核订单资料绑定
 * @param {number} bindingId - 绑定ID
 * @param {object} data - 请求参数
 * @param {boolean} data.approved - 是否通过
 * @param {string} [data.reviewRemark] - 审核备注
 */
export function reviewOrderMaterialBinding(bindingId, data) {
  return request.put(`/order/materials/bindings/${bindingId}/review`, data)
}

/**
 * 解绑订单资料
 * @param {number} bindingId - 绑定ID
 * @param {string} [reason] - 解绑原因
 */
export function unbindOrderMaterial(bindingId, reason) {
  return request.delete(`/order/materials/bindings/${bindingId}`, {
    params: {
      reason: reason || undefined
    }
  })
}

// ── 服务步骤 API ──

/**
 * 查询订单跟踪完整视图（步骤+批注）
 * @param {number} orderId - 订单ID
 */
export function getOrderTracking(orderId) {
  return request.get('/service-step/tracking', { params: { orderId } })
}

/**
 * 创建服务步骤
 * @param {object} data - 请求参数
 * @param {number} data.orderId - 订单ID
 * @param {string} data.name - 步骤名称
 * @param {string} [data.content] - 步骤内容
 * @param {number} [data.sort] - 排序号
 * @param {string} [data.nextRemindAt] - 下次提醒时间 yyyy-MM-dd HH:mm:ss
 */
export function createServiceStep(data) {
  return request.post('/service-step', data)
}

/**
 * 更新服务步骤
 * @param {object} data - 请求参数
 * @param {number} data.id - 步骤ID
 * @param {string} [data.name] - 步骤名称
 * @param {string} [data.content] - 步骤内容
 * @param {number} [data.sort] - 排序号
 * @param {number} [data.status] - 状态 0待处理 1进行中 2已完成
 * @param {string} [data.remark] - 备注
 * @param {string} [data.nextRemindAt] - 下次提醒时间
 */
export function updateServiceStep(data) {
  return request.put('/service-step', data)
}

/**
 * 删除服务步骤
 * @param {number} id - 步骤ID
 */
export function deleteServiceStep(id) {
  return request.delete(`/service-step/${id}`)
}

/**
 * 更新步骤状态
 * @param {object} data - 请求参数
 * @param {number} data.id - 步骤ID
 * @param {number} data.status - 状态 0待处理 1进行中 2已完成
 */
export function updateStepStatus(data) {
  return request.put('/service-step/status', data)
}

/**
 * 创建批注（订单级或步骤级）
 * @param {object} data - 请求参数
 * @param {string} data.bizType - 业务类型 ORDER / ORDER_STEP
 * @param {number} data.bizId - 业务ID（orderId 或 stepId）
 * @param {string} [data.title] - 标题
 * @param {string} data.content - 批注内容
 * @param {string} [data.nextRemindAt] - 下次提醒时间
 */
export function createOrderAnnotation(data) {
  return request.post('/service-step/annotation', data)
}
